import React from "react";
import { store } from "app/store";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import rehypeHighlight from "rehype-highlight";
import rehypeRaw from "rehype-raw";
import "katex/dist/katex.min.css";

const MarkdownView = ({ source, escapeHtml, style }) => {
  function markdownAsEscapedSequence(str) {
    const specialChars = /[!()[\]`\-*+=~_#|]|[0-9]/g;
    if (!!str) {
      return str?.replace(specialChars, function (match) {
        const string = "" + match.charCodeAt(0) || "00";
        const prefix = "&#";
        const suffix = ";";
        return prefix + string + suffix;
      });
    }
    return "";
  }

  const dark_mode = store.getState().settings.dark_mode;
  const colors = store.getState().settings.colors;

  return (
    <ReactMarkdown
      style={style}
      className={
        dark_mode == "dark_mode" ? "dm-markdown dark-mode" : "dm-markdown"
      }
      children={source}
      skipHtml={escapeHtml}
      remarkPlugins={[remarkGfm, remarkMath]}
      rehypePlugins={[rehypeKatex, rehypeHighlight, rehypeRaw]}
      components={{
        // Table styling
        table: ({ node, ...props }) => (
          <div style={{ overflowX: "auto", margin: "8px 0" }}>
            <table
              style={{
                minWidth: "100%",
                borderCollapse: "separate",
                borderSpacing: 0,
                borderColor: colors.border_color
              }}
              {...props}
            />
          </div>
        ),
        thead: ({ node, ...props }) => (
          <thead
            style={{
              backgroundColor: colors.gray_selected_color
            }}
            {...props}
          />
        ),
        tbody: ({ node, ...props }) => (
          <tbody
            style={{
              borderTopWidth: "1px",
              borderColor: colors.border_color
            }}
            {...props}
          />
        ),
        tr: ({ node, ...props }) => (
          <tr
            style={{
              transition: "background-color 0.2s"
            }}
            {...props}
          />
        ),
        th: ({ node, ...props }) => (
          <th
            style={{
              padding: "8px 12px",
              textAlign: "left",
              fontSize: "0.875rem",
              fontWeight: "600",
              color: colors.title_color
            }}
            {...props}
          />
        ),
        td: ({ node, ...props }) => (
          <td
            style={{
              padding: "8px 12px",
              borderTopWidth: "1px",
              borderColor: colors.border_color
            }}
            {...props}
          />
        ),

        // Link styling
        a: ({ node, ...props }) => (
          <a
            style={{
              color: colors.actionable_text_color,
              textDecoration: "underline",
              transition: "color 0.2s"
            }}
            target={props.href?.startsWith("http") ? "_blank" : undefined}
            rel={
              props.href?.startsWith("http") ? "noopener noreferrer" : undefined
            }
            {...props}
          />
        ),

        // Code block styling
        code: ({ node, className, children, ...props }) => {
          const match = /language-(\w+)/.exec(className || "");
          return !props.inline && match ? (
            <div
              style={{
                borderRadius: "6px",
                backgroundColor:
                  dark_mode === "dark_mode"
                    ? colors.card_color
                    : colors.gray_selected_color,
                margin: "8px 0",
                overflow: "hidden"
              }}
            >
              <div
                style={{
                  padding: "8px 16px",
                  fontSize: "0.75rem",
                  borderBottomWidth: "1px",
                  borderColor: colors.border_color,
                  color: colors.light_text_color,
                  backgroundColor:
                    dark_mode === "dark_mode"
                      ? colors.background_color
                      : colors.gray_selected_color
                }}
              >
                {match[1]}
              </div>
              <pre style={{ padding: "16px", overflowX: "auto" }}>
                <code className={className} {...props}>
                  {children}
                </code>
              </pre>
            </div>
          ) : (
            <code
              style={
                props.inline
                  ? {
                      padding: "2px 4px",
                      borderRadius: "4px",
                      backgroundColor:
                        dark_mode === "dark_mode"
                          ? colors.background_color
                          : colors.light_grey_color,
                      color: colors.text_color
                    }
                  : {}
              }
              className={!props.inline ? className : undefined}
              {...props}
            >
              {children}
            </code>
          );
        },

        // Heading styling
        h1: ({ node, ...props }) => (
          <p
            style={{ marginBottom: "8px", color: colors.text_color }}
            {...props}
          />
        ),
        h2: ({ node, ...props }) => (
          <p
            style={{ marginBottom: "8px", color: colors.text_color }}
            {...props}
          />
        ),
        h3: ({ node, ...props }) => (
          <p
            style={{ marginBottom: "8px", color: colors.text_color }}
            {...props}
          />
        ),

        // Paragraph and list styling
        p: ({ node, ...props }) => (
          <p
            style={{ marginBottom: "8px", color: colors.text_color }}
            {...props}
          />
        ),
        ul: ({ node, ...props }) => (
          <ul
            style={{
              listStyleType: "disc",
              paddingLeft: "24px",
              marginBottom: "8px",
              color: colors.text_color
            }}
            {...props}
          />
        ),
        ol: ({ node, ...props }) => (
          <ol
            style={{
              listStyleType: "decimal",
              paddingLeft: "24px",
              marginBottom: "8px",
              color: colors.text_color
            }}
            {...props}
          />
        ),
        li: ({ node, ...props }) => (
          <li
            style={{ marginBottom: "4px", color: colors.text_color }}
            {...props}
          />
        ),

        // Blockquote styling
        blockquote: ({ node, ...props }) => (
          <blockquote
            style={{
              borderLeftWidth: "4px",
              borderColor: colors.border_color,
              paddingLeft: "16px",
              fontStyle: "italic",
              margin: "8px 0",
              color: colors.light_text_color
            }}
            {...props}
          />
        )
      }}
    />
  );
};

export { MarkdownView };
